import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { alertsProfileApi } from '@/api/services/alertsProfile';
import { tsAlertTemplateLoaderApi } from '@/api/services/tsAlertTemplateLoader';
import type { CreateTraderSurveillanceProfileInput } from '@/api/types/traderSurveillance';

export const alertsProfileKeys = {
  all: ['alertsProfile'] as const,
  active: ['alertsProfile', 'active'] as const,
  deleted: ['alertsProfile', 'deleted'] as const,
  templates: ['alertsProfile', 'templates'] as const,
};

const LIST_PAGE = { skipCount: 0, maxResultCount: 1000 };

export function usePriorities() {
  return useQuery({ queryKey: ['alertsProfile', 'priorities'], queryFn: alertsProfileApi.getPriorities, staleTime: Infinity });
}

export function useActiveProfiles() {
  return useQuery({ queryKey: alertsProfileKeys.active, queryFn: () => alertsProfileApi.getActiveProfiles(LIST_PAGE) });
}

export function useDeletedProfiles() {
  return useQuery({ queryKey: alertsProfileKeys.deleted, queryFn: () => alertsProfileApi.getDeletedProfiles(LIST_PAGE) });
}

export function useEnabledTemplates() {
  return useQuery({ queryKey: alertsProfileKeys.templates, queryFn: tsAlertTemplateLoaderApi.getEnabledTemplates, staleTime: 5 * 60_000 });
}

export function useCreateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTraderSurveillanceProfileInput) => alertsProfileApi.createProfile(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsProfileKeys.all }),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateTraderSurveillanceProfileInput }) => alertsProfileApi.updateProfile(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsProfileKeys.all }),
  });
}

export function useDeleteProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => alertsProfileApi.deleteProfile(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsProfileKeys.all }),
  });
}

export function useSetProfileEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => alertsProfileApi.setProfileEnabled(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsProfileKeys.active }),
  });
}
